import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { customizationsAPI, formatCustomizationMoney } from '../../api/customizationsAPI';
import AuthenticatedReferenceImage from '../../components/customizations/AuthenticatedReferenceImage';
import BoutiqueBriefView from '../../components/customizations/BoutiqueBriefView';
import DynamicOfferingFields from '../../components/customizations/DynamicOfferingFields';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { fetchCart } from '../../store/slices/cartSlice';
import { getOfferingVertical } from './offeringCustomizationVertical';

const Page = styled.main`
  background: #eef6f3;
  min-height: calc(100vh - 120px);
  padding: 2rem 1rem 5rem;
`;
const Container = styled.div`margin: 0 auto; max-width: ${(p) => (p.$wide ? '1100px' : '780px')};`;
const Back = styled(Link)`color: #0f3d36; display: inline-block; font-weight: 800; margin-bottom: 1.25rem;`;
const Title = styled.h1`color: #0f3d36; font-size: clamp(1.8rem, 4vw, 2.4rem); margin: 0 0 0.65rem;`;
const Heading = styled.h2`color: #1e293b; font-size: 1.25rem; margin: 1.4rem 0 0.55rem;`;
const Body = styled.p`color: #475569; line-height: 1.65; margin: 0 0 1rem;`;
const Muted = styled.p`color: #64748b; font-size: 0.88rem; line-height: 1.5; margin: 0.35rem 0;`;
const Card = styled.section`
  background: #fff; border: 1px solid #d7e5e0; border-radius: 1rem;
  box-shadow: 0 8px 24px rgba(15, 61, 54, 0.05); padding: 1.15rem;
`;
const Stack = styled.div`display: grid; gap: 1rem;`;
const Grid = styled.div`display: grid; gap: 1rem; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));`;
const Button = styled.button`
  background: ${(p) => (p.$secondary ? '#fff' : '#0f3d36')};
  border: 1.5px solid #0f3d36; border-radius: 0.65rem;
  color: ${(p) => (p.$secondary ? '#0f3d36' : '#fff')};
  cursor: pointer; font: inherit; font-weight: 850; min-height: 46px; padding: 0.7rem 1rem;
  &:disabled { cursor: not-allowed; opacity: 0.55; }
`;
const ButtonLink = styled(Link)`
  align-items: center; background: ${(p) => (p.$secondary ? '#fff' : '#0f3d36')};
  border: 1.5px solid #0f3d36; border-radius: 0.65rem;
  color: ${(p) => (p.$secondary ? '#0f3d36' : '#fff')};
  display: inline-flex; font-weight: 850; justify-content: center; min-height: 46px;
  padding: 0 1rem; text-decoration: none;
`;
const Actions = styled.div`display: flex; flex-wrap: wrap; gap: 0.7rem; margin-top: 1.25rem;`;
const Label = styled.label`color: #334155; display: grid; font-size: 0.9rem; font-weight: 800; gap: 0.4rem;`;
const Input = styled.input`
  background: #fff; border: 1px solid #cbd5e1; border-radius: 0.65rem; font: inherit;
  min-height: 46px; padding: 0.7rem 0.8rem; width: 100%;
`;
const Textarea = styled.textarea`
  background: #fff; border: 1px solid #cbd5e1; border-radius: 0.65rem; font: inherit;
  min-height: 125px; padding: 0.8rem; resize: vertical; width: 100%;
`;
const Progress = styled.div`background: #d5e3df; border-radius: 999px; height: 7px; margin: 0.5rem 0 1.5rem; overflow: hidden;`;
const ProgressFill = styled.div`background: #0f3d36; height: 100%; width: ${(p) => `${p.$value}%`};`;
const ErrorBanner = styled.div`
  background: #fee2e2; border-radius: 0.65rem; color: #991b1b; line-height: 1.5;
  margin: 1rem 0; padding: 0.8rem 1rem;
`;
const Notice = styled.div`
  background: #e7f4ef; border-radius: 0.75rem; color: #204f45; line-height: 1.5; padding: 0.9rem 1rem;
`;
const Chips = styled.div`display: flex; flex-wrap: wrap; gap: 0.55rem; margin: 0.75rem 0 1rem;`;
const Chip = styled.button`
  background: ${(p) => (p.$active ? '#0f3d36' : '#fff')}; border: 1px solid #6f9088;
  border-radius: 999px; color: ${(p) => (p.$active ? '#fff' : '#334155')}; cursor: pointer;
  font-weight: 750; padding: 0.45rem 0.8rem;
`;
const Badge = styled.span`
  background: #e7f4ef; border-radius: 999px; color: #0f3d36; display: inline-block;
  font-size: 0.8rem; font-weight: 850; padding: 0.4rem 0.7rem;
`;
const ReferenceGrid = styled.div`display: grid; gap: 0.8rem; grid-template-columns: repeat(auto-fill, minmax(145px, 1fr));`;
const Reference = styled.div`background: #fff; border: 1px solid #e2e8f0; border-radius: 0.7rem; overflow: hidden;`;
const SearchRow = styled.div`display: flex; flex-wrap: wrap; gap: 0.65rem; margin-bottom: 1rem;`;

const apiMessage = (error, fallback = 'Something went wrong. Try again.') => {
  const data = error?.response?.data;
  if (typeof data?.error === 'string') return data.error;
  if (typeof data?.detail === 'string') return data.detail;
  if (data && typeof data === 'object') {
    const first = Object.values(data).flat().find((value) => typeof value === 'string');
    if (first) return first;
  }
  return fallback;
};

const STEPS = 4;

function BuyerOfferingIntroPage({ verticalKey = 'boutique' }) {
  const vertical = getOfferingVertical(verticalKey);
  return (
    <Page>
      <Container>
        <Back to={vertical.browseBackPath}>← Back to {vertical.titleCasePlural}</Back>
        <Title>Request a custom order</Title>
        <Body>
          Choose a {vertical.label} that accepts custom work, pick what they offer, describe your request, and ask for a quote.
        </Body>
        <Stack>
          {[
            ['1', `Choose a ${vertical.label}`, `Browse ${vertical.labelPlural} that take custom orders.`],
            ['2', 'Pick an offering', `See what that ${vertical.label} can make for you.`],
            ['3', 'Get a quote', 'Share details and references, then wait for their price.'],
          ].map(([number, title, body]) => (
            <Card key={number}>
              <Badge>{number}</Badge>
              <Heading>{title}</Heading>
              <Muted>{body}</Muted>
            </Card>
          ))}
        </Stack>
        <Notice style={{ marginTop: '1rem' }}>
          You won’t pay until you accept the {vertical.label}’s quote.
        </Notice>
        <Actions>
          <ButtonLink to={vertical.pickerPath}>Browse {vertical.labelPlural}</ButtonLink>
          <ButtonLink $secondary to="/customization-requests">My customization requests</ButtonLink>
        </Actions>
      </Container>
    </Page>
  );
}

function BuyerOfferingPickerPage({ verticalKey = 'boutique' }) {
  const vertical = getOfferingVertical(verticalKey);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shops, setShops] = useState([]);
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const activeTag = searchParams.get('tag') || '';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await vertical.listShops({
        q: searchParams.get('q') || undefined,
        tag: searchParams.get('tag') || undefined,
      });
      setShops(data.results || []);
    } catch (err) {
      setError(apiMessage(err));
    } finally {
      setLoading(false);
    }
  }, [searchParams, vertical]);

  useEffect(() => { load(); }, [load]);

  const tags = useMemo(() => {
    const all = [];
    shops.forEach((shop) => {
      (shop.tags || []).forEach((tag) => {
        if (!all.includes(tag)) all.push(tag);
      });
    });
    return all;
  }, [shops]);

  return (
    <Page>
      <Container $wide>
        <Back to={vertical.introPath}>← Back</Back>
        <Title>Choose a {vertical.label}</Title>
        <Body>Start with the {vertical.label}. Their offerings and form fields come next.</Body>
        <SearchRow>
          <Input
            style={{ maxWidth: 360 }}
            placeholder={`Search ${vertical.labelPlural} or offerings`}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                const next = new URLSearchParams(searchParams);
                if (query.trim()) next.set('q', query.trim());
                else next.delete('q');
                setSearchParams(next);
              }
            }}
          />
          <Button
            type="button"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              if (query.trim()) next.set('q', query.trim());
              else next.delete('q');
              setSearchParams(next);
            }}
          >
            Search
          </Button>
        </SearchRow>
        {tags.length ? (
          <Chips>
            <Chip
              type="button"
              $active={!activeTag}
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete('tag');
                setSearchParams(next);
              }}
            >
              All
            </Chip>
            {tags.map((tag) => (
              <Chip
                key={tag}
                type="button"
                $active={activeTag === tag}
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.set('tag', tag);
                  setSearchParams(next);
                }}
              >
                {tag}
              </Chip>
            ))}
          </Chips>
        ) : null}
        {error ? <ErrorBanner>{error}</ErrorBanner> : null}
        {loading ? <LoadingSpinner /> : (
          <Grid>
            {shops.map((shop) => (
              <Card
                key={shop.id}
                as="button"
                style={{ cursor: 'pointer', textAlign: 'left' }}
                onClick={() => navigate(vertical.shopOfferingsPath(shop.id))}
              >
                <Heading style={{ marginTop: 0 }}>{shop.brand_name}</Heading>
                <Muted>{shop.bio || 'Custom orders available.'}</Muted>
                <Muted>
                  {(shop.offerings || []).length} offering{(shop.offerings || []).length === 1 ? '' : 's'}
                  {shop.customization_min_price_cents != null
                    ? ` · from ${formatCustomizationMoney(shop.customization_min_price_cents)}`
                    : shop.pricing_to_be_discussed
                      ? ' · pricing discussed'
                      : ''}
                </Muted>
                <Chips>
                  {(shop.tags || []).slice(0, 4).map((tag) => <Badge key={tag}>{tag}</Badge>)}
                </Chips>
              </Card>
            ))}
          </Grid>
        )}
        {!loading && !shops.length ? (
          <Notice>No {vertical.labelPlural} are accepting custom orders right now.</Notice>
        ) : null}
      </Container>
    </Page>
  );
}

function BuyerOfferingOfferingsPage({ verticalKey = 'boutique' }) {
  const vertical = getOfferingVertical(verticalKey);
  const { shopId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [startingId, setStartingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await vertical.listOfferingsPublic(shopId);
        if (!cancelled) setPayload(data);
      } catch (err) {
        if (!cancelled) setError(apiMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shopId, vertical]);

  const start = async (offeringId) => {
    setStartingId(offeringId);
    setError('');
    try {
      const draft = await vertical.createDraft({
        shop_id: Number(shopId),
        offering_id: offeringId,
      });
      navigate(vertical.wizardPath(draft.id, 1), { replace: true });
    } catch (err) {
      setError(apiMessage(err));
      setStartingId(null);
    }
  };

  if (loading) {
    return <Page><Container><LoadingSpinner /></Container></Page>;
  }

  return (
    <Page>
      <Container>
        <Back to={vertical.pickerPath}>← Back to {vertical.labelPlural}</Back>
        <Title>{payload?.shop?.brand_name || `${vertical.titleCase} offerings`}</Title>
        <Body>Pick what you want this {vertical.label} to make. Your brief will use their form fields.</Body>
        {error ? <ErrorBanner>{error}</ErrorBanner> : null}
        <Stack>
          {(payload?.results || []).map((offering) => (
            <Card key={offering.id}>
              <Heading style={{ marginTop: 0 }}>{offering.title}</Heading>
              <Muted>{offering.description || 'No description provided.'}</Muted>
              {offering.min_price_cents != null ? (
                <Muted>From {formatCustomizationMoney(offering.min_price_cents)}</Muted>
              ) : null}
              <Chips>
                {(offering.tags || []).map((tag) => <Badge key={tag}>{tag}</Badge>)}
              </Chips>
              <Actions>
                <Button type="button" disabled={startingId === offering.id} onClick={() => start(offering.id)}>
                  {startingId === offering.id ? 'Starting…' : 'Choose this offering'}
                </Button>
              </Actions>
            </Card>
          ))}
        </Stack>
        {!payload?.results?.length ? <Notice>This {vertical.label} has no active offerings.</Notice> : null}
      </Container>
    </Page>
  );
}

function BuyerOfferingWizardPage({ verticalKey = 'boutique' }) {
  const vertical = getOfferingVertical(verticalKey);
  const { requestId, step: routeStep } = useParams();
  const navigate = useNavigate();
  const step = Math.min(STEPS, Math.max(1, Number(routeStep) || 1));
  const [request, setRequest] = useState(null);
  const [description, setDescription] = useState('');
  const [answers, setAnswers] = useState({});
  const [special, setSpecial] = useState('');
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const data = await customizationsAPI.getBuyerRequest(requestId);
    if (data.kind !== vertical.kind) {
      navigate(`/customization-requests/${data.id}`, { replace: true });
      return;
    }
    setRequest(data);
    setDescription(data.boutique_brief?.description || '');
    setAnswers(data.boutique_brief?.field_answers || {});
    setSpecial(data.special_requests || '');
    setBudgetMin(data.budget_min_cents != null ? String(data.budget_min_cents / 100) : '');
    setBudgetMax(data.budget_max_cents != null ? String(data.budget_max_cents / 100) : '');
  }, [navigate, requestId, vertical.kind]);

  useEffect(() => {
    load().catch((err) => setError(apiMessage(err)));
  }, [load]);

  const fields = request?.offering?.fields_schema || [];

  const savePayload = () => ({
    special_requests: special,
    budget_min_cents: budgetMin ? Math.round(Number(budgetMin) * 100) : null,
    budget_max_cents: budgetMax ? Math.round(Number(budgetMax) * 100) : null,
    boutique_brief: {
      description,
      field_answers: answers,
    },
  });

  const validateStep = () => {
    const next = {};
    if (step === 1 && !description.trim()) next.description = 'Describe what you want made.';
    if (step === 2) {
      fields.forEach((field) => {
        const value = answers[field.key];
        const empty = value == null || value === '' || (Array.isArray(value) && !value.length);
        if (field.required && empty) next[field.key] = 'This field is required.';
      });
    }
    if (step === 3) {
      if (!budgetMin || Number(budgetMin) <= 0) next.budgetMin = 'Enter a positive minimum.';
      if (!budgetMax || Number(budgetMax) <= 0) next.budgetMax = 'Enter a positive maximum.';
      if (Number(budgetMin) > Number(budgetMax)) next.budgetMax = 'Max must be at least the minimum.';
    }
    setFieldErrors(next);
    return !Object.keys(next).length;
  };

  const continueStep = async () => {
    if (!validateStep()) return;
    setSaving(true);
    setError('');
    try {
      const updated = await customizationsAPI.updateDraft(requestId, savePayload());
      setRequest(updated);
      if (step < STEPS) {
        navigate(vertical.wizardPath(requestId, step + 1));
      }
    } catch (err) {
      setError(apiMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const sendRequest = async () => {
    if (!validateStep()) return;
    setSaving(true);
    setError('');
    try {
      await customizationsAPI.updateDraft(requestId, savePayload());
      await customizationsAPI.submitDraft(requestId);
      const sent = await customizationsAPI.sendBuyerRequest(requestId);
      toast.success(`Request sent to the ${vertical.label}.`);
      navigate(`/customization-requests/${sent.id}`, { replace: true });
    } catch (err) {
      setError(apiMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setError('');
    try {
      await customizationsAPI.uploadReferences(requestId, files);
      await load();
    } catch (err) {
      setError(apiMessage(err));
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  if (!request) {
    return <Page><Container>{error ? <ErrorBanner>{error}</ErrorBanner> : <LoadingSpinner />}</Container></Page>;
  }

  return (
    <Page>
      <Container>
        <Back to={vertical.shopOfferingsPath(request.selected_shop?.id)}>← Back</Back>
        <Title>Your custom order</Title>
        <Muted>
          {request.selected_shop?.brand_name} · {request.offering?.title} · Step {step} of {STEPS}
        </Muted>
        <Progress><ProgressFill $value={(step / STEPS) * 100} /></Progress>
        {error ? <ErrorBanner>{error}</ErrorBanner> : null}

        {step === 1 ? (
          <Card>
            <Heading style={{ marginTop: 0 }}>Describe your request</Heading>
            <Label>
              What do you want made?
              <Textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Share the item, style, and details that matter most." />
            </Label>
            {fieldErrors.description ? <ErrorBanner>{fieldErrors.description}</ErrorBanner> : null}
          </Card>
        ) : null}

        {step === 2 ? (
          <Card>
            <Heading style={{ marginTop: 0 }}>Details for this offering</Heading>
            {fields.length ? (
              <DynamicOfferingFields
                fields={fields}
                values={answers}
                errors={fieldErrors}
                onChange={setAnswers}
              />
            ) : (
              <Muted>No extra fields for this offering. Continue to references and budget.</Muted>
            )}
          </Card>
        ) : null}

        {step === 3 ? (
          <Stack>
            <Card>
              <Heading style={{ marginTop: 0 }}>Reference images (optional)</Heading>
              <Input type="file" accept="image/*" multiple disabled={uploading} onChange={onUpload} />
              <ReferenceGrid style={{ marginTop: '1rem' }}>
                {(request.references || []).map((reference) => (
                  <Reference key={reference.id}>
                    <AuthenticatedReferenceImage
                      url={reference.url}
                      reference={reference}
                      alt={reference.original_filename || 'Reference image'}
                      aspect="1"
                      style={{ width: '100%', aspectRatio: '1', objectFit: 'cover' }}
                    />
                    <Button
                      $secondary
                      type="button"
                      style={{ width: '100%', borderRadius: 0 }}
                      onClick={async () => {
                        await customizationsAPI.deleteReference(request.id, reference.id);
                        await load();
                      }}
                    >
                      Remove
                    </Button>
                  </Reference>
                ))}
              </ReferenceGrid>
            </Card>
            <Card>
              <Heading style={{ marginTop: 0 }}>Budget and notes</Heading>
              <Grid>
                <Label>
                  Budget minimum (EGP)
                  <Input value={budgetMin} onChange={(event) => setBudgetMin(event.target.value)} />
                  {fieldErrors.budgetMin ? <ErrorBanner>{fieldErrors.budgetMin}</ErrorBanner> : null}
                </Label>
                <Label>
                  Budget maximum (EGP)
                  <Input value={budgetMax} onChange={(event) => setBudgetMax(event.target.value)} />
                  {fieldErrors.budgetMax ? <ErrorBanner>{fieldErrors.budgetMax}</ErrorBanner> : null}
                </Label>
              </Grid>
              <Label style={{ marginTop: '1rem' }}>
                Special requests (optional)
                <Textarea value={special} onChange={(event) => setSpecial(event.target.value)} />
              </Label>
            </Card>
          </Stack>
        ) : null}

        {step === 4 ? (
          <Card>
            <Heading style={{ marginTop: 0 }}>Review and send</Heading>
            <BoutiqueBriefView
              brief={{ description, field_answers: answers }}
              offering={request.offering}
              references={request.references}
            />
            <Muted style={{ marginTop: '1rem' }}>
              Budget {formatCustomizationMoney(Math.round(Number(budgetMin || 0) * 100))}
              {' – '}
              {formatCustomizationMoney(Math.round(Number(budgetMax || 0) * 100))}
            </Muted>
            {special ? <Muted>Notes: {special}</Muted> : null}
          </Card>
        ) : null}

        <Actions>
          {step > 1 ? (
            <Button $secondary type="button" onClick={() => navigate(vertical.wizardPath(requestId, step - 1))}>
              Back
            </Button>
          ) : null}
          {step < STEPS ? (
            <Button type="button" disabled={saving} onClick={continueStep}>
              {saving ? 'Saving…' : 'Continue'}
            </Button>
          ) : (
            <Button type="button" disabled={saving} onClick={sendRequest}>
              {saving ? 'Sending…' : 'Send request'}
            </Button>
          )}
        </Actions>
      </Container>
    </Page>
  );
}

export function BuyerBoutiqueIntroPage() {
  return <BuyerOfferingIntroPage verticalKey="boutique" />;
}
export function BuyerBoutiquePickerPage() {
  return <BuyerOfferingPickerPage verticalKey="boutique" />;
}
export function BuyerBoutiqueOfferingsPage() {
  return <BuyerOfferingOfferingsPage verticalKey="boutique" />;
}
export function BuyerBoutiqueWizardPage() {
  return <BuyerOfferingWizardPage verticalKey="boutique" />;
}

export function BuyerDesignerIntroPage() {
  return <BuyerOfferingIntroPage verticalKey="designer" />;
}
export function BuyerDesignerPickerPage() {
  return <BuyerOfferingPickerPage verticalKey="designer" />;
}
export function BuyerDesignerOfferingsPage() {
  return <BuyerOfferingOfferingsPage verticalKey="designer" />;
}
export function BuyerDesignerWizardPage() {
  return <BuyerOfferingWizardPage verticalKey="designer" />;
}

export function BuyerBoutiqueRequestExtras({ request, onChanged }) {
  const dispatch = useDispatch();
  if (!request || (request.kind !== 'boutique' && request.kind !== 'designer')) return null;
  return (
    <Card style={{ marginTop: '1rem' }}>
      <Heading style={{ marginTop: 0 }}>Custom order brief</Heading>
      <BoutiqueBriefView
        brief={request.boutique_brief}
        offering={request.offering}
        references={request.references}
      />
      {request.state === 'quoted' ? (
        <Actions>
          <Button
            type="button"
            onClick={async () => {
              const result = await customizationsAPI.acceptQuote(request.id);
              if (result.added_to_cart) dispatch(fetchCart());
              toast.success('Quote accepted and added to cart.');
              onChanged?.(result);
            }}
          >
            Accept quote
          </Button>
          <Button
            $secondary
            type="button"
            onClick={async () => {
              const result = await customizationsAPI.declineQuote(request.id);
              toast.info('Quote declined.');
              onChanged?.(result);
            }}
          >
            Decline quote
          </Button>
        </Actions>
      ) : null}
    </Card>
  );
}
